import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useProducts, cartKeyFor } from "./ProductsContext";

const CartContext = createContext(null);
const KEY = "hhb_cart";

export function CartProvider({ children }) {
  const { products = [] } = useProducts() || {};
  const [stored, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(stored));
    } catch (e) {
      /* storage unavailable (private mode) — basket still works for this visit */
    }
  }, [stored]);

  // Show the current admin-set price/name for each line, not the one saved in the
  // browser when it was added. (Checkout always re-prices on the server anyway.)
  const items = useMemo(() => {
    if (!products.length) return stored;
    return stored.map((i) => {
      const p = products.find((x) => cartKeyFor(x) === i.id || x.id === i.id);
      const s = p?.sizes?.find((x) => x.size === i.size);
      return s ? { ...i, name: p.name, price: s.price } : i;
    });
  }, [stored, products]);

  const addItem = ({ id, name, size, price }, qty = 1) => {
    const add = Math.max(1, parseInt(qty, 10) || 1);
    setItems((cur) => {
      const key = `${id}__${size}`;
      if (cur.some((i) => i.key === key)) {
        return cur.map((i) => (i.key === key ? { ...i, qty: i.qty + add } : i));
      }
      return [...cur, { key, id, name, size, price, qty: add }];
    });
  };

  const removeItem = (key) => setItems((cur) => cur.filter((i) => i.key !== key));

  const setQty = (key, qty) =>
    setItems((cur) =>
      qty <= 0 ? cur.filter((i) => i.key !== key) : cur.map((i) => (i.key === key ? { ...i, qty } : i))
    );

  const clear = () => setItems([]);

  const count = items.reduce((n, i) => n + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, setQty, clear, count, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
